import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { WorkingHoursValidationDetailBuilder } from '../../models/builders/working-hours-validation-detail.builder';
import { WorkingHoursValidationBuilder } from '../../models/builders/working-hours-validation.builder';
import { WorkingHoursValidation } from '../../models/working-hours-validation';
import { WorkingHoursValidationDetail } from '../../models/working-hours-validation-detail';
import { WorkingHoursValidationDetailSummaryBuilder } from '../../models/builders/working-hours-validation-detail-summary.builder';
import { isArray } from 'util';
import { mapCollection } from '../../utils/collection.utils';
import { WorkingHoursValidationDetailSummary } from '../../models/working-hours-validation-detail-summary';
import { getUserLogged } from '../../utils/jwt.utils';

const workingHoursValidationDetailBody = (
  workingHoursValidationDetail: WorkingHoursValidationDetail,
  workingHourValidationHash: string
) => ({
  workingHourValidationHash: workingHourValidationHash,
  user: (workingHoursValidationDetail.userHash) ? workingHoursValidationDetail.userHash : getUserLogged(),
  hash: workingHoursValidationDetail.hash,
  baseManufacturingOrder: (workingHoursValidationDetail.manufacturingOrder) ? workingHoursValidationDetail.manufacturingOrder.hash : null,
  userActivity: (workingHoursValidationDetail.userActivity) ? workingHoursValidationDetail.userActivity.hash : null,
  userSubactivity:  (workingHoursValidationDetail.userSubactivity) ? workingHoursValidationDetail.userSubactivity.hash : null,
  day: workingHoursValidationDetail.day,
  totalTime: workingHoursValidationDetail.totalTime,
  model: workingHoursValidationDetail.modelHash,
  elementNumber: workingHoursValidationDetail.erpTask,
  nonProductiveManufacturingOrderCategory: workingHoursValidationDetail.nonProductiveManufacturingOrderCategoryHash,
  nonConformityCode: workingHoursValidationDetail.nonConformityCode,
  validated: workingHoursValidationDetail.validated || false
});

const createRefreshWorkingHoursValidationDetailsBody = (workingHoursValidationDetails: WorkingHoursValidationDetailSummary[]) => {
  const workingHours = (isArray(workingHoursValidationDetails))
    ? mapCollection((workingHoursValidation: WorkingHoursValidationDetailSummary) =>
      // tslint:disable-next-line: max-line-length
      ({
        hash: workingHoursValidation.hash,
        manufacturingOrder: workingHoursValidation.manufacturingOrderHash,
        userSubactivity: workingHoursValidation.userSubactivityHash
      }), workingHoursValidationDetails)
    : [];

  return { workingHours };
};

@Injectable()
export class WorkingHoursValidatorApiService {

  private apiUrl: string;

  constructor(private httpClient: HttpClient) {
    this.apiUrl = `${environment.apiUrl}workingHoursValidations`;
  }

  getWorkingHoursValidation(hash: string) {
    return this.httpClient.get(`${this.apiUrl}/${hash}`)
      .pipe(
        map(WorkingHoursValidationBuilder.fromJson)
      );
  }

  getWorkingHoursValidationsByRole(hash: string) {
    return this.httpClient.get(`${this.apiUrl}/role${hash}`)
      .pipe(
        map(WorkingHoursValidationBuilder.fromJson)
      );
  }

  getWorkingHoursValidations(userHash?: string) {
    const params = (userHash) ? new HttpParams().append('user', userHash) : null;

    return this.httpClient.get(this.apiUrl, { params })
      .pipe(
        map(WorkingHoursValidationBuilder.fromList)
      );
  }

  getWorkingHoursValidationDetails(hash: string) {
    return this.httpClient.get(`${this.apiUrl}/${hash}/workingHoursValidationDetails`)
      .pipe(
        map(WorkingHoursValidationDetailBuilder.fromList)
      );
  }

  getWorkingHoursValidationDetailSummary(hash: string) {
    return this.httpClient.get(`${this.apiUrl}/${hash}/summary`)
      .pipe(
        map(WorkingHoursValidationDetailSummaryBuilder.fromList)
      );
  }

  refreshWorkingHoursValidationDetails(hash: string, workingHoursValidationDetails: WorkingHoursValidationDetailSummary[]) {
    return this.httpClient.put<{ completed: boolean }>(`${this.apiUrl}/${hash}/refresh`,
      createRefreshWorkingHoursValidationDetailsBody(workingHoursValidationDetails))
      .pipe(
        map(data => data.completed)
      );
  }

  updateWorkingHoursValidationDetail(
    workingHoursValidationDetail: WorkingHoursValidationDetail,
    workingHourValidationHash: string
  ) {
    if (workingHoursValidationDetail.hash) {
      return this.httpClient.put(
        `${this.apiUrl}/detail/${workingHoursValidationDetail.hash}`,
        workingHoursValidationDetailBody(workingHoursValidationDetail, workingHourValidationHash)
      );
    } else {
      return this.httpClient.post(
        `${this.apiUrl}/detail`,
        workingHoursValidationDetailBody(workingHoursValidationDetail, workingHourValidationHash)
      );
    }
  }

  deleteWorkingHoursValidationDetail(
    workingHoursValidationDetail: string,
    workingHourValidationHash: string
  ) {
    return this.httpClient.delete(`${this.apiUrl}/deleteDetail/${workingHoursValidationDetail}/validation/${workingHourValidationHash}`);
  }

  validate(workingHoursValidation: WorkingHoursValidation) {
    return this.httpClient.put(`${this.apiUrl}/${workingHoursValidation.hash}/validate`, null);
  }
}



// WEBPACK FOOTER //
// /var/www/borneo/prod/borneo_app/src/app/core/services/api/working-hours-validator-api.service.ts